/**
 * iter971kI - Orphan Transactions Recovery Card
 *
 * When Mono returns more raw transactions than we've bridged into
 * bank_feed_transactions, this card surfaces the exact count and lets
 * the user trigger a one-click recovery. Root cause docs live in
 * `services/mono_orphan_recovery.py`.
 *
 * The card polls the dry-run endpoint every 30s so it appears the moment
 * a gap opens (e.g. mid-sync failure, webhook drop) and disappears the
 * moment the pipeline catches up.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { AlertTriangle, Loader2, RefreshCcw, CheckCircle2 } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { toast } from 'sonner';
import { Button } from '../ui/button';

const POLL_MS = 30000;

export default function OrphanRecoveryCard({ bankAccounts = [] }) {
  const [gaps, setGaps] = useState({});   // { [bank_account_id]: {orphans_detected, date_range, bank_name} }
  const [busyId, setBusyId] = useState(null);

  // Memoized on a stable key - a bare .filter() here created a new array on
  // every render, which gave checkAllGaps a new identity, re-ran the effect,
  // called setGaps, and looped forever ("Maximum update depth exceeded").
  const monoKey = bankAccounts
    .filter((a) => a.open_banking_source === 'mono' || a.open_banking_connected)
    .map((a) => a.id)
    .join(',');
  const monoAccounts = useMemo(
    () => bankAccounts.filter((a) => a.open_banking_source === 'mono' || a.open_banking_connected),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monoKey]
  );

  const checkAllGaps = useCallback(async () => {
    if (!monoAccounts.length) {
      setGaps((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }
    const next = {};
    await Promise.all(
      monoAccounts.map(async (a) => {
        try {
          const { data } = await apiClient.post(
            '/open-banking-feeds/mono/recover-orphans',
            { bank_account_id: a.id, dry_run: true }
          );
          if (data?.orphans_detected > 0) {
            next[a.id] = {
              orphans_detected: data.orphans_detected,
              date_range: data.date_range,
              bank_name: a.bank_name || 'your bank',
              account_name: a.account_name || '',
            };
          }
        } catch {
          // ignore - the card should never break the page
        }
      })
    );
    setGaps(next);
  }, [monoAccounts]);

  useEffect(() => {
    checkAllGaps();
    const id = setInterval(checkAllGaps, POLL_MS);
    return () => clearInterval(id);
  }, [checkAllGaps]);

  const handleRecover = async (bankAccountId, bankName) => {
    setBusyId(bankAccountId);
    try {
      const { data } = await apiClient.post(
        '/open-banking-feeds/mono/recover-orphans',
        { bank_account_id: bankAccountId, dry_run: false, max_batch: 2000 }
      );
      const n = data?.submitted_for_classification || 0;
      if (n > 0) {
        toast.success(
          `Recovery started: ${n} missing ${bankName} transaction${n === 1 ? '' : 's'} `
            + `are being classified. This can take a couple of minutes.`
        );
        // Drop this card immediately; polling will re-check in 30s.
        setGaps((prev) => {
          const copy = { ...prev };
          delete copy[bankAccountId];
          return copy;
        });
      } else {
        toast.info(
          data?.reason === 'all_before_accounting_start'
            ? `All missing rows are before your accounting start date: nothing to recover.`
            : `Nothing to recover right now.`
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recovery failed. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const entries = Object.entries(gaps);
  if (!entries.length) return null;

  return (
    <div
      className="space-y-2 mb-4"
      data-testid="orphan-recovery-card"
    >
      {entries.map(([baId, info]) => (
        <div
          key={baId}
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/40"
          data-testid={`orphan-recovery-tile-${baId}`}
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
              {info.orphans_detected.toLocaleString()} transaction
              {info.orphans_detected === 1 ? '' : 's'} missing from {info.bank_name}
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
              These were fetched from your bank but never made it into the Bank Feed.
              {info.date_range?.oldest && info.date_range?.newest && (
                <span> Range: {info.date_range.oldest.substring(0, 10)}
                  {' → '}{info.date_range.newest.substring(0, 10)}.</span>
              )}
              {' '}Click Recover to classify and post them now.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="flex-shrink-0 border-amber-400 bg-white text-amber-800 hover:bg-amber-100"
            disabled={busyId === baId}
            onClick={() => handleRecover(baId, info.bank_name)}
            data-testid={`orphan-recovery-btn-${baId}`}
          >
            {busyId === baId ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Recovering…</>
            ) : (
              <><RefreshCcw className="w-3.5 h-3.5 mr-1" /> Recover</>
            )}
          </Button>
        </div>
      ))}
    </div>
  );
}
